import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { WsEventsCases, WsRoomsCases } from '@bandido/shared/constants';

import { useWS } from '@frontend/features/common/hooks/useWS';
import { QueryKeys } from '@frontend/features/common/types';

import { CONFIG } from '@frontend/config';
import { CaseViewDesktop } from '../../features/cases/components/CaseViewDesktop';
import { CaseViewMobile } from '../../features/cases/components/CaseViewMobile';
import { useTheme, useMediaQuery } from '@mui/material';
import { CaseProvider } from '@frontend/features/cases/context';

export const CaseView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { isConnected, socketRef, sendMessage } = useWS(`${CONFIG.WS_URL}/cases`);
  const { id } = useParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isConnected) {
      sendMessage('joinRoom', WsRoomsCases.CASES);
    }

    socketRef.current?.on(WsEventsCases.CASE_CONTENT, (caseData: string) => {
      const newHistory = JSON.parse(caseData);

      if (newHistory.caseId === Number(id)) {
        const queries = queryClient
          .getQueryCache()
          .findAll({ queryKey: [QueryKeys.getCaseHistory, { caseId: Number(id) }], active: true });

        queries.forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, (oldData: any) => {
            if (!oldData?.pages) return oldData;

            const pages = [...oldData.pages];
            const items = [...pages[pages.length - 1].data.items, newHistory];
            pages[pages.length - 1].data = { ...pages[pages.length - 1].data, items };

            if (oldData) {
              return {
                ...oldData,
                pages,
              };
            }
          });
        });
      }
    });

    socketRef.current?.on(WsEventsCases.CASE_UPDATED, (caseData: string) => {
      const parsedCase = JSON.parse(caseData);

      queryClient.setQueryData([QueryKeys.getCaseById, Number(parsedCase.id)], (_oldData: unknown) => parsedCase);
      queryClient.invalidateQueries([QueryKeys.getCaseHistory, { caseId: Number(parsedCase.id) }]);
    });

    socketRef.current?.on(WsEventsCases.CASE_RELATION_UPDATED, (cases: number[]) => {
      if (cases.includes(Number(id))) {
        queryClient.invalidateQueries([QueryKeys.getRelatedCases, Number(id)]);
        queryClient.invalidateQueries([QueryKeys.getCaseHistory, { caseId: Number(id) }]);
      }
    });

    return () => {
      socketRef.current?.off(WsEventsCases.CASE_UPDATED);
      socketRef.current?.off(WsEventsCases.CASE_RELATION_UPDATED);
      socketRef.current?.off(WsEventsCases.CASE_CONTENT);
    };
  }, [sendMessage, isConnected, queryClient, id]);

  if (!id) {
    return null;
  }

  return <CaseProvider>{isMobile ? <CaseViewMobile /> : <CaseViewDesktop />}</CaseProvider>;
};
